import { PickSnippetArgsData, SelectionIds } from '@components/sportsline-pick-snippet/sportsline-pick-snippet';
import { isUndefinedOrNull } from './commonUtils';
import { League, Maybe, SportslineWebComponent_FreeExpertPickQuery, SportslineWebComponent_PickSnippetFragment, SportslineWebComponent_PickSnippetQuery } from '@lib/graphql-types';
import { SportslinePickSnippetCardProps } from 'src/components';
import { getGameAbbr } from './gameUtils';
import { formatSmartDate } from './dateUtils';
import { toHexColor } from './colorUtils';
import { capitalizeWords, stringWithFallback } from './stringUtils';
import { sportsBetSchema, sportsBetWithEntityInfo, validateSchema } from './sidelineSchemaUtils';

const overUnderColor = '#10101099';
export const defaultBackgroundColor = '#ce1141'; // Default color for the card background

export type SideLinesBase = {
  type: 'snippet';
  backgroundColor: string;
  odds: number;
  marketId: string | number;
  marketName: string;
  marketType: string;
  gameId: string;
};

export type SidelinesMoneyLine = SideLinesBase & {
  marketType: 'standard' | 'game';
  entityType: 'Team';
  entityId: string;
};

export type SidelinesSpread = SideLinesBase & {
  marketType: 'standard' | 'game';
  entityType: 'Team';
  entityId: string;
  line: number | string;
};

export type SidelinesTotal = SideLinesBase & {
  marketType: 'standard' | 'game';
  pick: 'over' | 'under';
  line: number | string;
};

export type SidelinesPlayerProp = SideLinesBase & {
  marketType: 'player';
  entityType: 'Player';
  entityId: string;
};

export type SidelinesPlayerPropOverUnder = SidelinesPlayerProp & {
  pick: 'over' | 'under';
};

export type SidelinesPlayerPropYesNo = SidelinesPlayerProp & {
  pick: 'yes' | 'no';
};

export type SidelinesTeamProp = SideLinesBase & {
  marketType: 'team';
  entityType: 'Team';
  entityId: string;
};

export type SidelinesTeamPropOverUnder = SidelinesTeamProp & {
  line: number | string;
  pick: 'over' | 'under';
};

export type SideLinesData =
  | SidelinesMoneyLine
  | SidelinesSpread
  | SidelinesTotal
  | SidelinesPlayerPropOverUnder
  | SidelinesPlayerPropYesNo
  | SidelinesTeamPropOverUnder
  | SidelinesPlayerProp
  | SidelinesTeamProp;

export type GetPropertySidelineDataArgs = {
  dataAttr?: string;
  marketName?: string;
  selectionIds: SelectionIds;
  backgroundColor: string;
};

export function getDataFromAttribute(dataAttr: GetPropertySidelineDataArgs['dataAttr']): PickSnippetArgsData | null {
  try {
    const value = JSON.parse(dataAttr);
    return {
      type: value.type,
      gameId: value.gameId,
      odds: parseFloat(value.odds),
      marketName: value.marketName ? value.marketName.toLowerCase() : null,
      marketId: value.marketId ? value.marketId : null,
      playerId: value.playerId ? value.playerId : null,
      teamId: value.teamId ? value.teamId : null,
      vendorMarketId: value.vendorMarketId ? value.vendorMarketId : null,
      vendorPlayerId: value.vendorPlayerId ? value.vendorPlayerId : null,
      vendorTeamId: value.vendorTeamId ? value.vendorTeamId : null,
      pick: value.pick ? value.pick.toLowerCase() : null,
      marketType: value.marketType,
      vendorGameId: value.vendorGameId,
      line: value.line ? parseFloat(value.line) : null,
      selectionId: value.selectionId,
    };
  } catch (error) {
    console.warn('Invalid data-pick attribute format:', error);
    return null;
  }
}

export function getPlayerFromPickSnippet(data?: SportslineWebComponent_PickSnippetQuery['pickSnippet']) {
  if (isUndefinedOrNull(data) || isUndefinedOrNull(data.selection)) {
    return null;
  }
  if (data.selection.__typename === 'PickSnippetSelectionPlayerProp') {
    return data.selection.player;
  }
  return null;
}

export const getVendorTeamId = (
  team: SportslineWebComponent_PickSnippetQuery['pickSnippet']['game']['homeTeam'] | SportslineWebComponent_PickSnippetQuery['pickSnippet']['game']['awayTeam'],
) => {
  if (isUndefinedOrNull(team?.vendors?.edges)) {
    return null;
  }
  const sortedEdges = team.vendors.edges
    .filter(edge => !isUndefinedOrNull(edge?.vendorTeamId))
    .slice() // Create a shallow copy to avoid mutating the original array
    .sort((a, b) => a.vendorTeamId.localeCompare(b.vendorTeamId));
  return sortedEdges[0]?.vendorTeamId ?? null;
};

type PlayerArg = Maybe<{
  id: string;
  vendors?: {
    edges: {
      vendorPlayerId?: string;
      leagueId?: number;
    }[];
  };
}>;

export const findPlayerVendorIdByLeague = (player: PlayerArg, leagueId: League['id']): string | null => {
  if (isUndefinedOrNull(player)) {
    return null;
  }
  const edges = [...(player.vendors?.edges ?? [])].reverse().filter(Boolean);
  const vendorEdge = edges.find(edge => edge.leagueId?.toString() === leagueId?.toString());
  return vendorEdge?.vendorPlayerId ?? edges[0]?.vendorPlayerId ?? null;
};

type GetSelectionIdsArgs = {
  dataAttr: string;
  data?: SportslineWebComponent_PickSnippetQuery['pickSnippet'];
};

export function getSelectionIdsFromData({ dataAttr = '', data }: GetSelectionIdsArgs): SelectionIds {
  const player = getPlayerFromPickSnippet(data);
  const team = getActiveTeamFromData({ data, attr: dataAttr });
  const attr = getDataFromAttribute(dataAttr);
  const game = data?.game;
  const result: SelectionIds = {
    gameId: game?.id ?? attr?.gameId ?? null,
    playerId: player?.id ?? attr?.playerId ?? null,
    teamId: team?.id ?? null,
    vendorGameId: game?.vendors?.edges?.[0]?.vendorGameId ?? attr?.vendorGameId ?? null,
    vendorPlayerId: findPlayerVendorIdByLeague(player, game?.league?.id) ?? attr?.vendorPlayerId ?? null,
    vendorTeamId: getVendorTeamId(team),
  };
  return result;
}

type GetActiveTeamFromDataArgs = {
  data?: SportslineWebComponent_PickSnippetQuery['pickSnippet'];
  attr?: string;
};

export function getActiveTeamFromData({ data = null, attr = '' }: GetActiveTeamFromDataArgs) {
  if (isUndefinedOrNull(data) || isUndefinedOrNull(data.selection)) {
    return null;
  }
  const teams = [data.game?.homeTeam, data.game?.awayTeam].filter(Boolean);
  const attributeData = getDataFromAttribute(attr);
  if (data.selection.__typename === 'PickSnippetSelectionPlayerProp') {
    return teams.find(t => t.id?.toString().toLowerCase() === attributeData?.teamId?.toString().toLowerCase()) ?? null;
  }

  if (data.selection.team && Boolean(data.selection.team.id)) {
    // Note: This is a type guard to ensure that the selection has a team with an id
    return teams.find(t => t.id?.toString().toLowerCase() === (data.selection as unknown as any).team.id.toString().toLowerCase()) ?? null;
  }

  return null;
}

export function isOverOrUnderPick(pick: string = ''): pick is 'over' | 'under' {
  const normalizedPick = stringWithFallback(pick, '').toLowerCase();
  return normalizedPick === 'over' || normalizedPick === 'under';
}

export function getPropertySidelineData({ dataAttr, marketName, selectionIds, backgroundColor }: GetPropertySidelineDataArgs): SideLinesData | null {
  const attributeData = getDataFromAttribute(dataAttr);
  if (!Boolean(attributeData)) return null;

  const isOverUnder = isOverOrUnderPick(attributeData.pick);

  const base: SideLinesBase = {
    type: 'snippet',
    backgroundColor,
    odds: attributeData.odds,
    marketId: attributeData.vendorMarketId,
    marketName: toMarketName(stringWithFallback(marketName, attributeData.marketName)),
    marketType: attributeData.marketType,
    gameId: selectionIds.vendorGameId,
  };

  // TODO: TYPES NEED TO BE FIXED they are being hardcoded
  switch (attributeData.marketType) {
    case 'standard':
    case 'game': {
      const data = {
        ...base,
        marketType: attributeData.marketType,
      };

      if (isOverUnder) {
        const result = {
          ...data,
          line: attributeData.line,
          pick: attributeData.pick as 'over' | 'under',
        } satisfies SidelinesTotal;

        return validateSchema(sportsBetSchema)(result);
      }

      const result = {
        ...base,
        marketType: attributeData.marketType,
        entityType: 'Team',
        entityId: selectionIds.vendorTeamId,
        ...(!isUndefinedOrNull(attributeData.line) && {
          line: attributeData.line,
        }),
      } satisfies SidelinesMoneyLine | SidelinesSpread;

      return validateSchema(sportsBetWithEntityInfo)(result);
    }

    case 'player': {
      const result = {
        ...base,
        marketType: attributeData.marketType,
        entityType: 'Player',
        pick: attributeData.pick as 'over' | 'under' | 'yes' | 'no',
        entityId: selectionIds.vendorPlayerId,
        ...(!isUndefinedOrNull(attributeData.line) && {
          line: attributeData.line,
        }),
      } satisfies SidelinesPlayerProp | SidelinesPlayerPropOverUnder | SidelinesPlayerPropYesNo;
      return validateSchema(sportsBetWithEntityInfo)(result);
    }

    case 'team': {
      const data: SidelinesTeamProp = {
        ...base,
        marketType: attributeData.marketType,
        entityType: 'Team',
        entityId: selectionIds.vendorTeamId,
      };

      if (isOverUnder) {
        return validateSchema(sportsBetWithEntityInfo)({
          ...data,
          line: attributeData.line,
          pick: attributeData.pick as 'over' | 'under',
        });
      }

      return validateSchema(sportsBetWithEntityInfo)({
        ...data,
        entityId: selectionIds.vendorTeamId,
        ...(isUndefinedOrNull(attributeData.line) ? {} : { line: attributeData.line }),
      });
    }

    default:
      return null;
  }
}

export function getType(data: SportslineWebComponent_PickSnippetFragment) {
  switch (data?.selection?.__typename) {
    case 'PickSnippetSelectionPlayerProp':
      return 'player';
    case 'PickSnippetSelectionTeamProp':
      return 'team';
    case 'PickSnippetSelectionGameProp':
      return 'game';
    default:
      return 'standard';
  }
}

export function getGameInfoLabel(data: SportslineWebComponent_PickSnippetFragment) {
  const label = getGameAbbr({
    homeTeam: data.game.homeTeam,
    awayTeam: data.game.awayTeam,
    isNeutral: data.game.isNeutral,
  });

  return [label, formatSmartDate(new Date(data.game.scheduledTime))].join(' • ');
}

export function getPlayerName(data: SportslineWebComponent_PickSnippetFragment | null): string | null {
  try {
    if (data?.selection?.__typename === 'PickSnippetSelectionPlayerProp') {
      const player = getPlayerFromPickSnippet(data);
      return [player.firstName, player.lastName].filter(Boolean).join(' ').trim();
    }
  } catch (error) {
    console.error('Error getting player name:', error);
  }
  return null;
}

const toMarketName = (marketName: string | null): string => capitalizeWords(stringWithFallback(marketName, '-'));

type FromPickSnippetToPickSnippetCardPropsArgs = {
  data: SportslineWebComponent_PickSnippetFragment;
  dataAttr?: string;
};

type FromPickSnippetToPickSnippetCardPropsResponses = Omit<SportslinePickSnippetCardProps, 'loading'> & {
  sideLinesData: SideLinesData | null;
};

export const fromPickSnippetToPickSnippetCardProps = ({ data, dataAttr }: FromPickSnippetToPickSnippetCardPropsArgs): FromPickSnippetToPickSnippetCardPropsResponses => {
  try {
    let activeColor = defaultBackgroundColor;
    const attr = getDataFromAttribute(dataAttr);
    const type = getType(data);
    const gameInfoLabel = getGameInfoLabel(data);
    const selectionIds = getSelectionIdsFromData({ dataAttr, data });
    const activeTeam = getActiveTeamFromData({ data, attr: dataAttr });
    const teamName = !isUndefinedOrNull(activeTeam) ? [activeTeam.mediumName, activeTeam.nickname, activeTeam.shortName].find(Boolean) ?? '' : '';
    const playerName = getPlayerName(data);
    const playerId = !isUndefinedOrNull(selectionIds.playerId) ? -1 : null;
    const teamId = activeTeam?.id ?? null;
    const isOverOrUnder = ['over', 'under'].includes(attr?.pick?.toLowerCase() ?? '');
    const label = data.selection?.label || null;
    const labelLong = data.selection?.labelLong || null;
    const marketLabel = toMarketName(data.selection?.marketName ?? attr?.marketName);
    const odds = data.selection?.odds;
    const side = data.selection?.side as unknown as any;
    if (Boolean(type === 'game' || type === 'standard') && isOverOrUnder) {
      activeColor = overUnderColor;
    } else {
      activeColor = toHexColor({ color: activeTeam?.colorHexDex, fallback: activeColor });
    }

    return {
      gameInfoLabel,
      type,
      playerId,
      teamId,
      teamName,
      playerName,
      activeColor,
      label,
      labelLong,
      marketLabel,
      odds,
      side,
      sideLinesData: getPropertySidelineData({
        dataAttr,
        marketName: marketLabel,
        selectionIds,
        backgroundColor: activeColor,
      }),
    };
  } catch (error) {
    console.error('Error fromPickSnippetToPickSnippetCardProps:', error);
  }

  return null;
};

export const getMarketIdFromFreeExpertPick = (freeExpertPick?: SportslineWebComponent_FreeExpertPickQuery['freeExpertPick']): string | null => {
  try {
    const markets = freeExpertPick?.selection?.market?.vendors?.edges ?? [];
    if (!isUndefinedOrNull(freeExpertPick?.league?.id)) {
      return markets.find(edge => edge.league.id === freeExpertPick.league.id)?.vendorMarketId || null;
    }
  } catch (error) {
    console.warn('Error getting market ID from free expert pick:', error);
  }
  return null;
};

export const fromFreeExpertPicksToSidelinesData = (freeExpertPick?: SportslineWebComponent_FreeExpertPickQuery['freeExpertPick']): SideLinesData | null => {
  if (!Boolean(freeExpertPick?.selection?.odds)) {
    return null;
  }

  const gameId = freeExpertPick.game.vendors?.edges?.[0]?.vendorGameId;
  const marketId = getMarketIdFromFreeExpertPick(freeExpertPick);
  const marketName = toMarketName(stringWithFallback(freeExpertPick.selection.subLabel));
  const base: Omit<SideLinesData, 'line' | 'entityType' | 'entityId' | 'marketType'> = {
    type: 'snippet',
    marketId,
    marketName,
    gameId,
    odds: Number(freeExpertPick.selection.odds),
    backgroundColor: defaultBackgroundColor, // Default background color
    ...(!isUndefinedOrNull(freeExpertPick.selection.value) ? { line: freeExpertPick.selection.value } : {})
  };

  switch (freeExpertPick.selection.__typename) {
    case 'ExpertPickSelectionGameProps':
    case 'ExpertPickSelectionStandard': {
      const isOverUnder = isOverOrUnderPick(freeExpertPick.selection.side);
      const marketType = freeExpertPick.selection.__typename === 'ExpertPickSelectionGameProps' ? 'game' : 'standard';

      if (isOverUnder) {
        return validateSchema(sportsBetSchema)({
          ...base,
          marketType,
          line: freeExpertPick.selection.value,
          pick: freeExpertPick.selection.side.toLowerCase() as 'over' | 'under',
          backgroundColor: overUnderColor,
        });
      }

      const result = {
        ...base,
        marketType,
        entityType: 'Team',
        entityId: freeExpertPick.selection.team?.vendors?.edges?.[0]?.vendorTeamId ?? null,
        backgroundColor: toHexColor({ color: freeExpertPick.selection.team?.colorHexDex, fallback: base.backgroundColor }),
      } satisfies SidelinesMoneyLine | SidelinesSpread;

      if (!validateSchema(sportsBetWithEntityInfo)(result)) {
        console.error('Invalid result schema:', result);
        return null;
      }

      return validateSchema(sportsBetWithEntityInfo)(result);
    }
    case 'ExpertPickSelectionTeamProps': {
      const entityId = freeExpertPick.selection.team?.vendors?.edges?.[0]?.vendorTeamId;
      const result = {
        ...base,
        marketType: 'team',
        entityType: 'Team',
        entityId,
        pick: freeExpertPick.selection.side.toLowerCase() as 'over' | 'under',
        backgroundColor: toHexColor({ color: freeExpertPick.selection.team?.colorSecondaryHex, fallback: base.backgroundColor }),
      } satisfies SidelinesTeamProp | SidelinesTeamPropOverUnder;
      return validateSchema(sportsBetWithEntityInfo)(result);
    }
    case 'ExpertPickSelectionPlayerProps': {
      if ('player' in freeExpertPick.selection && isOverOrUnderPick(freeExpertPick.selection.side)) {
        const entityType = 'Player';
        const entityId = findPlayerVendorIdByLeague(freeExpertPick.selection.player, freeExpertPick.league.id);
        const result = {
          ...base,
          marketType: 'player',
          entityType,
          entityId,
          pick: freeExpertPick.selection.side.toLowerCase() as 'over' | 'under' | 'yes' | 'no',
          backgroundColor: toHexColor({ color: freeExpertPick.selection.playerTeam?.colorSecondaryHex, fallback: base.backgroundColor }),
        } satisfies SidelinesPlayerProp | SidelinesPlayerPropOverUnder | SidelinesPlayerPropYesNo;
        return validateSchema(sportsBetWithEntityInfo)(result);
      }
      return null;
    }
    default:
      return null;
  }
};

export const isFreeExpertPickBettable = (freeExpertPick?: SportslineWebComponent_FreeExpertPickQuery['freeExpertPick']): boolean => {
  // Removing this check to push the button placement to production  
  // if (`${process.env.TARGET_ENV}`.toLowerCase() === 'prod') {
  //   return false;
  // }
  return !!fromFreeExpertPicksToSidelinesData(freeExpertPick);
};
